import { AddNewAccount } from "components/ChartOfAccounts/AddNewAccount";
import { CategoryLabel } from "components/design/CategoryLabel";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import {
  coaOptions,
  CategoryIndent,
  TransactionComboboxControl,
  DropdownIndicator,
  TransactionComboboxClearIndicator,
} from "components/Transaction/TransactionColumn";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useModal } from "hooks/useModal";
import { useWindowSize } from "hooks/useWindowSize";
import {
  CSSObjectWithLabel,
  MenuPlacement,
  MultiValue,
  SingleValue,
} from "react-select";
import { getMobileMenuPortalStyles } from "util/getMobileMenuPortalStyles";
import { HiddenCategoryTypes } from "store/apis/chartOfAccounts";
import { TxnCategories } from "types/Models/books";
import { flattenTypes } from "utils/flattenCOA";
import { stopPropagation } from "utils/stopPropagation";

const MENU_WIDTH = 450;

export const CategorySelector = ({
  name,
  category,
  hiddenCategories = [],
  root_category_id,
  label,
  hideError = true,
  onChange: onChangeFromProps,
  withForm = true,
  isDisabled = false,
  hideAction = false,
  hiddenCategoryTypes = ["BANK_ACCOUNT"],
  size = "regular",
  menuPortalStyle,
  isUpdating,
  required = false,
  outline = false,
  isClearable = true,
  customCategoryOptions = [],
  placeholder = "Select",
  enableTopLevel = false,
  menuPlacement,
}: {
  name?: string;
  label?: string;
  category?: string | null;
  root_category_id?: string;
  hideAction?: boolean;
  hiddenCategories?: string[];
  hiddenCategoryTypes?: HiddenCategoryTypes[];
  hideError?: boolean;
  onChange?: (
    value: {
      label: string;
      value: string;
      categoryType: string;
      data: string;
      name: string;
    } | null
  ) => void;
  withForm?: boolean;
  isDisabled?: boolean;
  size?: "small" | "large" | "regular";
  menuPortalStyle?: Record<string, string | number>;
  isUpdating?: boolean;
  required?: boolean;
  outline?: boolean;
  isClearable?: boolean;
  customCategoryOptions?: TxnCategories[];
  placeholder?: string;
  enableTopLevel?: boolean;
  menuPlacement?: MenuPlacement;
}) => {
  const { isOpen, close, open } = useModal();
  const { isMobile } = useWindowSize();

  const getMenuPortalStyles = (
    base: CSSObjectWithLabel
  ): CSSObjectWithLabel => {
    if (menuPortalStyle) return { ...base, ...menuPortalStyle };
    if (isMobile) return getMobileMenuPortalStyles(base, MENU_WIDTH);
    return { ...base, width: MENU_WIDTH };
  };
  const { chartOfAccounts = [], isLoading } = useChartOfAccounts({
    hiddenCategoryTypes,
    root_category_id,
  });

  const categoryOptions =
    customCategoryOptions && customCategoryOptions.length > 0
      ? customCategoryOptions
      : chartOfAccounts;

  const defaultAssignedCategory = flattenTypes({
    accounts: categoryOptions,
  }).find(({ uuid }) => uuid === category);

  const onChange = (
    value:
      | SingleValue<{
          label: string;
          value: string;
          categoryType: string;
          data: string;
          name: string;
        }>
      | MultiValue<OptionData>
  ) => {
    if (value instanceof Array) {
      return;
    }

    onChangeFromProps?.(value);
  };

  return (
    <div className="t-group t-select-none" onClick={(e) => e.stopPropagation()}>
      <Combobox
        onKeyDown={stopPropagation}
        aria-label="Category selector"
        isLoading={isLoading || isUpdating}
        isClearable={isClearable}
        actions={
          !hideAction && (
            <Button customType="link" onClick={open}>
              Add a new account
            </Button>
          )
        }
        styles={{
          menuPortal: getMenuPortalStyles,
        }}
        components={
          outline
            ? {
                Control: TransactionComboboxControl,
                DropdownIndicator,
                ClearIndicator: TransactionComboboxClearIndicator,
              }
            : {}
        }
        name={name}
        withForm={withForm}
        hideError={hideError}
        required={required}
        // @ts-ignore - options not updated based on this usecase
        onChange={onChange}
        placeholder={placeholder}
        menuPlacement={menuPlacement}
        menuPortalTarget={document.body}
        options={coaOptions(
          categoryOptions,
          0,
          !enableTopLevel,
          hiddenCategories
        )}
        filterOption={(v, i) =>
          v.data.data?.toLocaleLowerCase().includes(i.toLocaleLowerCase()) ||
          false
        }
        isDisabled={isDisabled || isLoading}
        label={label}
        value={
          category
            ? {
                label: (
                  <CategoryIndent
                    label={
                      <CategoryLabel
                        indentLevel={0}
                        category={defaultAssignedCategory}
                      />
                    }
                    indentLevel={0}
                  />
                ),
                value: defaultAssignedCategory?.uuid || "",
              }
            : null
        }
        size={size}
      />
      {isOpen && (
        <AddNewAccount
          isOpen={isOpen}
          close={close}
          open={open}
          onSuccess={() => close()}
        />
      )}
    </div>
  );
};
